import React, { Key, useEffect, useRef } from 'react';
import { Autocomplete, AutocompleteItem, Avatar } from "@heroui/react";
import { useTranslation } from 'react-i18next';
import { useCountriesService } from '../../../services/countriesService/countries.service';
import useDebouncedCallback from '../../../hooks/useDebouncedCallback';
import { CountriesEntity } from '@bitecritic-api/src/countries/countries.entity';
import { useLocale } from '~/hooks/useLocale';

interface CountryAutocompleteProps {
  onChange: (country: CountriesEntity | undefined) => void;
  value: number | undefined;
  label: string;
  isRequired?: boolean;
  isInvalid?: boolean;
  errorMessage?: string;
  defaultCountryCode?: string;
  useCurrentLocale?: boolean;
}

export default function CountryAutocomplete({
  onChange,
  value,
  label,
  isRequired,
  isInvalid,
  errorMessage,
  defaultCountryCode,
  useCurrentLocale = true,
}: CountryAutocompleteProps) {
  const { t } = useTranslation();
  const [search, setSearch] = React.useState('');
  const { data: countries, isLoading } = useCountriesService();
  const { currentLocale } = useLocale();
  const hasInitialized = useRef(false);
  const userCleared = useRef(false);

  const debouncedSetSearch = useDebouncedCallback((value: string) => {
    setSearch(value);
  }, 300);

  // Set the initial country based on currentLocale or defaultCountryCode
  useEffect(() => {
    if (countries && !value && !userCleared.current) {
      let countryToSelect: CountriesEntity | undefined;

      if (defaultCountryCode) {
        countryToSelect = countries.find(c => c.code === defaultCountryCode);
      } else if (useCurrentLocale && currentLocale?.code) {
        const localeCountryCode = currentLocale.code.split('-')[1];
        countryToSelect = countries.find(c => c.code === localeCountryCode);
      }

      if (countryToSelect && !hasInitialized.current) {
        onChange(countryToSelect);
        hasInitialized.current = true;
      }
    }
  }, [countries, value, onChange, currentLocale, defaultCountryCode, useCurrentLocale]);

  const filteredCountries = React.useMemo(() => {
    if (!countries) return [];
    if (!search) return countries;

    const searchLower = search.toLowerCase();
    return countries.filter(country =>
      country.name.toLowerCase().includes(searchLower)
    );
  }, [countries, search]);

  const handleSelectionChange = (countryId: Key | null) => {
    if (countryId === null) {
      userCleared.current = true;
      onChange(undefined);
    } else {
      userCleared.current = false;
      const selectedCountry = countries?.find(country => country.id.toString() === countryId);
      onChange(selectedCountry);
    }
  };

  return (
    <Autocomplete
      label={label}
      placeholder={t('profile.selectCountry') ?? ''}
      onSelectionChange={handleSelectionChange}
      selectedKey={value?.toString()}
      isLoading={isLoading}
      onInputChange={debouncedSetSearch}
      isRequired={isRequired}
      isInvalid={isInvalid}
      errorMessage={errorMessage}
    >
      {filteredCountries.map((country) => (
        <AutocompleteItem
          key={country.id.toString()}
          startContent={
            <Avatar
              alt={country.name}
              className="w-6 h-6"
              src={`/images/country-flags/${country.code.toLowerCase()}.png`}
            />
          }
        >
          {country.name}
        </AutocompleteItem>
      ))}
    </Autocomplete>
  );
} 